/**
 * Utility functions for parsing and formatting PostgreSQL INTERVAL durations
 * Used for task window durations and crop phase durations
 */

/**
 * Parse a PostgreSQL INTERVAL string into days
 *
 * Supports formats:
 * - "30 days"
 * - "2 weeks"
 * - "1 mon" / "1 month"
 * - "1 year"
 *
 * @param {string} durationString - PostgreSQL INTERVAL format
 * @returns {number} Duration in days
 */
export const parseDuration = (durationString) => {
  if (!durationString || typeof durationString !== 'string') {
    return 0
  }

  const trimmed = durationString.trim().toLowerCase()

  // Match number and unit: "30 days", "2 weeks", etc.
  const match = trimmed.match(/^(\d+)\s*(\w+)$/)

  if (!match) {
    console.warn(`Unable to parse duration: "${durationString}"`)
    return 0
  }

  const value = parseInt(match[1], 10)
  const unit = match[2]

  // Convert to days based on unit
  switch (unit) {
    case 'day':
    case 'days':
      return value

    case 'week':
    case 'weeks':
      return value * 7

    case 'mon':
    case 'mons':
    case 'month':
    case 'months':
      return value * 30 // Approximate

    case 'year':
    case 'years':
      return value * 365 // Approximate

    default:
      console.warn(`Unknown duration unit: "${unit}" in "${durationString}"`)
      return 0
  }
}

/**
 * Format a number of days into PostgreSQL INTERVAL string
 *
 * @param {number} days - Duration in days
 * @returns {string} PostgreSQL INTERVAL format ("X days")
 */
export const formatDuration = (days) => {
  if (typeof days !== 'number' || days < 0) {
    return '0 days'
  }

  return `${days} days`
}

/**
 * Convert weeks to PostgreSQL INTERVAL string
 *
 * @param {number} weeks - Duration in weeks
 * @returns {string} PostgreSQL INTERVAL format ("X days")
 */
export const formatWeeks = (weeks) => {
  return formatDuration(weeks * 7)
}
